import { useMemo } from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Box, DataTable, Checkbox } from '@shopify/polaris';

const permissionsList = [
    { name: 'poView', label: 'View Purchase Orders' },
    { name: 'poUpdate', label: 'Update Purchase Orders' },
    { name: 'transferView', label: 'View Store-to-Store Transfers' },
    { name: 'transferCreate', label: 'Create Store-to-Store Transfers' },
    { name: 'transferUpdate', label: 'Update Store-to-Store Transfers' },
    { name: 'ccView', label: 'View Cycle Counts' },
    { name: 'ccCreatePrescribe', label: 'Prescribe Cycle Counts' },
    { name: 'ccCreateAdHoc', label: 'Create Ad Hoc Cycle Counts' },
    { name: 'ccStart', label: 'Start Cycle Counts' },
    { name: 'ccUpdate', label: 'Perform Cycle Counts' },
    { name: 'ccManagerApprove', label: 'Manager Approval of Cycle Counts' },
    { name: 'ccFinanceApprove', label: 'Finance Approval of Cycle Counts' },
    { name: 'adjView', label: 'View Inventory Adjustments' },
    { name: 'adjCreate', label: 'Create Inventory Adjustments' },
    { name: 'adjApprove', label: 'Approve Inventory Adjustments' },
    { name: 'programView', label: 'View Voucher Programs' },
    { name: 'programEdit', label: 'Edit Voucher Programs' },
    { name: 'settingApiKeys', label: 'Create API Keys' },
    { name: 'settingPermissions', label: 'Administrate User Permissions' },
    { name: 'settingReasonCodes', label: 'Administrate Reason Codes' },
    { name: 'settingCCThreshold', label: 'Administrate Cycle Count Threshold Settings' },
    { name: 'jobsView', label: 'View & Run Jobs' },
    { name: 'reportsView', label: 'View & Use Reports' },
];

const RoleMatrixTab = () => {
    const queryClient = useQueryClient();
    const rolesQueryKey = ['adminRoles'];

    const fetchRoles = async () => {
        const rolesRes = await axios.get(`${import.meta.env.VITE_API_URL}/api/app/admin/roles`);
        return rolesRes.data.data.roles;
    };

    const { data: rolesData = [], isLoading } = useQuery({
        queryKey: rolesQueryKey,
        queryFn: fetchRoles,
        staleTime: Infinity, // Keep data fresh indefinitely
        gcTime: Infinity,
    });

    const updatePermissionMutation = useMutation({
        mutationFn: async ({ roleId, permission, value }) => {
            await axios.patch(`${import.meta.env.VITE_API_URL}/api/app/admin/roles/${roleId}`, {
                permission,
                value,
            });
        },
        retry: 2,
        onMutate: async ({ roleId, permission, value }) => {
            await queryClient.cancelQueries({ queryKey: rolesQueryKey });

            const previousRoles = queryClient.getQueryData(rolesQueryKey);

            queryClient.setQueryData(rolesQueryKey, (current) => {
                if (!Array.isArray(current)) return current;
                return current.map((role) => (role.id === roleId ? { ...role, [permission]: value } : role));
            });

            return { previousRoles };
        },
        onError: (error, variables, context) => {
            if (context?.previousRoles) {
                queryClient.setQueryData(rolesQueryKey, context.previousRoles);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: rolesQueryKey, refetchType: 'none' });
        },
    });

    const handlePermissionToggle = (roleId, permissionName, currentValue) => {
        updatePermissionMutation.mutate({
            roleId,
            permission: permissionName,
            value: !currentValue,
        });
    };

    const filteredRoles = useMemo(() => rolesData.filter((role) => role.roleName !== 'None'), [rolesData]);

    const rows = permissionsList.map((permission) => [
        permission.label,
        ...filteredRoles.map((role) => (
            <Checkbox
                key={`role-${role.id}-permission-${permission.name}`}
                label=""
                checked={!!role[permission.name]}
                onChange={() => handlePermissionToggle(role.id, permission.name, role[permission.name])}
                disabled={role.roleName === 'Super Admin'}
            />
        )),
    ]);

    if (isLoading) {
        return <Card>Loading...</Card>;
    }

    return (
        <Card title="Permissions Matrix">
            <Box className="permission-matrix">
                <DataTable className="" columnContentTypes={['text', ...filteredRoles.map(() => 'text')]} headings={['Permission', ...filteredRoles.map((role) => role.roleName)]} rows={rows} />
            </Box>
        </Card>
    );
};

export default RoleMatrixTab;
